import { Component, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SelectItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Observable, Subject, takeUntil } from 'rxjs';

import { IPaperAuthorCreateDialogData } from 'editing-tool/src/app/shared/components/paper/steps/authors/author-create-dialog/paper-author-create-dialog-data';
import { CommonSelectItemsService, ValidationConstants, ValidationService } from 'sc-common';
import { MembersClient } from 'sc-common/core/services/open-api/main-api-client';
import { ApiGridOrganization } from 'sc-common/core/services/open-api/shared-api-client';

@Component({
    templateUrl: './paper-author-create-dialog.component.html',
    standalone: false
})
export class PaperAuthorCreateDialogComponent implements OnDestroy {

    public requestInProcess: boolean;

    public readonly form: UntypedFormGroup;

    public readonly countries$: Observable<SelectItem[]>;

    public readonly titles$: Observable<SelectItem[]>;

    private readonly _dialogData: IPaperAuthorCreateDialogData;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        selectItemsService: CommonSelectItemsService,
        dialogConfig: DynamicDialogConfig,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _dialogRef: DynamicDialogRef,
        private readonly _membersClient: MembersClient) {

        this.countries$ = selectItemsService.countries$;
        this.titles$ = selectItemsService.memberTitles$;
        this._dialogData = dialogConfig.data as IPaperAuthorCreateDialogData;
        this.form = this._buildForm();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public create(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.requestInProcess = true;

        this._dialogData.create(this.form.value)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._dialogRef.close(true));
    }

    public fillAddressFromGrid(gridAddress: ApiGridOrganization): void {

        this.form.patchValue({
            countryId: gridAddress.countryCode,
            city: gridAddress.city,
            zip: gridAddress.zip
        });

        this.form.markAllAsTouched();
    }

    private _buildForm(): UntypedFormGroup {
        return this._formBuilder.group({
            title: null,
            firstName: ['', [
                Validators.required,
                ValidationService.nameValidator,
                Validators.maxLength(ValidationConstants.Max50Length)]],
            lastName: ['', [
                Validators.required,
                ValidationService.nameValidator,
                Validators.maxLength(ValidationConstants.Max50Length)]],
            email: ['', {
                validators: [
                    Validators.required,
                    Validators.email,
                    Validators.maxLength(ValidationConstants.MaxEmailLength)],
                asyncValidators: [ValidationService.uniqueEmailWithHint(email => this._membersClient.isEmailUnused(email))]
            }],
            addressLine1: ['', [
                Validators.maxLength(ValidationConstants.Max200Length),
                ValidationService.alphaNumSymbolValidator
            ]],
            city: ['', [
                Validators.maxLength(ValidationConstants.Max50Length),
                ValidationService.addressValidator
            ]],
            countryId: [null, Validators.required],
            zip: ['', [
                Validators.maxLength(ValidationConstants.Max20Length),
                ValidationService.zipVatValidator
            ]],
            universityOrOrganization: ['', [
                Validators.required,
                Validators.maxLength(ValidationConstants.Max200Length),
                ValidationService.alphaNumSymbolValidator
            ]],
            department: ['', [
                Validators.maxLength(ValidationConstants.Max200Length),
                ValidationService.alphaNumSymbolValidator
            ]],
            orcidId: [null, {
                validators: ValidationService.orcidValidator,
                asyncValidators: ValidationService.isOrcidIdUnusedValidator(orcidId => this._dialogData.isOrcidIdUnusedRequest(orcidId))
            }]
        });
    }
}
